import { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft,
  Cloud,
  FileText,
  History,
  LayoutGrid,
  Network,
  PanelLeft,
  WandSparkles,
} from 'lucide-react'
import { nextGraphPosition, summaryFromContent, uid } from '../lib/utils'
import { AI_ROLES } from '../lib/ai'
import { Avatar, MiniCover } from './Shared'
import Binder from './Binder'
import BoardView from './BoardView'
import GraphView from './GraphView'
import DocumentView from './DocumentView'
import Inspector from './Inspector'
import SplitModal from './SplitModal'
import MergeModal from './MergeModal'
import NewCardModal from './NewCardModal'

const VIEWS = [
  { id: 'board', label: '看板', icon: LayoutGrid },
  { id: 'graph', label: '关联', icon: Network },
  { id: 'document', label: '文稿', icon: FileText },
]

function dedupe(links) {
  const seen = new Set()
  return links.filter((link) => {
    if (!link || link.source === link.target) return false
    const key = [link.source, link.target].sort().join('|')
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export default function Workspace({
  project,
  view,
  setView,
  saveState,
  onBack,
  onUpdateProject,
  onOpenRoundtable,
  onOpenFinish,
  onOpenHistory = null,
  notify,
}) {
  const SAVE_TEXT = { saving: '正在保存', saved: '已同步到云端', offline: '离线未同步', error: '同步失败', local: '已保存到本地' }
  const [selectedCardId, setSelectedCardId] = useState(null)
  const [inspectorOpen, setInspectorOpen] = useState(false)
  const [selectMode, setSelectMode] = useState(false)
  const [selection, setSelection] = useState([])
  const [splitCardId, setSplitCardId] = useState(null)
  const [mergeIds, setMergeIds] = useState([])
  const [newCardFor, setNewCardFor] = useState(null)
  const [binderOpen, setBinderOpen] = useState(false)

  const selectedCard = project.cards.find((card) => card.id === selectedCardId) || null
  const splitCard = project.cards.find((card) => card.id === splitCardId) || null
  const mergeCards = project.cards.filter((card) => mergeIds.includes(card.id))
  const acts = useMemo(() => Array.from(new Set(project.cards.map((card) => card.act).filter(Boolean))), [project.cards])
  const validSelected = selectedCard && project.cards.some((card) => card.id === selectedCardId)

  useEffect(() => {
    if (selectedCardId && !validSelected) {
      setInspectorOpen(false)
      if (view === 'document') setSelectedCardId(project.cards[0]?.id || null)
      else setSelectedCardId(null)
    }
    if (view === 'document' && !selectedCardId && project.cards[0]) {
      setSelectedCardId(project.cards[0].id)
    }
  }, [project.cards, selectedCardId, validSelected, view])

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape' && inspectorOpen) setInspectorOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [inspectorOpen])

  useEffect(() => {
    const ids = new Set(project.cards.map((card) => card.id))
    setSelection((current) => {
      const next = current.filter((id) => ids.has(id))
      return next.length === current.length ? current : next
    })
    if (splitCardId && !ids.has(splitCardId)) setSplitCardId(null)
    if (mergeIds.length && !mergeIds.every((id) => ids.has(id))) setMergeIds([])
  }, [project.cards, splitCardId, mergeIds])

  function updateProject(updater) {
    onUpdateProject(updater)
  }

  function patchCard(cardId, patch) {
    const now = new Date().toISOString()
    updateProject((current) => ({
      ...current,
      updatedAt: now,
      cards: current.cards.map((card) => {
        if (card.id !== cardId) return card
        const next = { ...card, ...patch, updatedAt: now }
        if (Object.prototype.hasOwnProperty.call(patch, 'content')) {
          next.summary = summaryFromContent(patch.content, card.summary)
        }
        return next
      }),
    }))
  }

  function openCard(cardId) {
    setSelectedCardId(cardId)
    setInspectorOpen(true)
    setBinderOpen(false)
  }

  function openDocument(cardId) {
    setSelectedCardId(cardId)
    setInspectorOpen(false)
    setView('document')
  }

  function changeView(nextView) {
    if (nextView === 'document' && !selectedCardId && project.cards[0]) setSelectedCardId(project.cards[0].id)
    if (nextView !== 'board') {
      setSelectMode(false)
      setSelection([])
    }
    setView(nextView)
  }

  function pickFromBinder(cardId) {
    setSelectedCardId(cardId)
    setInspectorOpen(false)
    setView('document')
  }

  function moveCard(cardId, column) {
    if (!project.cards.some((card) => card.id === cardId && card.column !== column)) return
    const now = new Date().toISOString()
    updateProject((current) => ({
      ...current,
      updatedAt: now,
      cards: current.cards.map((card) => card.id === cardId ? { ...card, column, updatedAt: now } : card),
    }))
  }

  function movePosition(cardId, graph) {
    const now = new Date().toISOString()
    updateProject((current) => ({
      ...current,
      updatedAt: now,
      cards: current.cards.map((card) => card.id === cardId ? { ...card, graph, updatedAt: now } : card),
    }))
  }

  function autoArrange(updates) {
    const now = new Date().toISOString()
    updateProject((current) => ({
      ...current,
      updatedAt: now,
      cards: current.cards.map((card) => updates[card.id] ? { ...card, graph: updates[card.id], updatedAt: now } : card),
    }))
  }

  function addConnection(source, target, label = '相关') {
    if (!source || !target || source === target) return false
    const exists = project.connections.some((link) =>
      (link.source === source && link.target === target) || (link.source === target && link.target === source)
    )
    if (exists) {
      notify('这两张卡片已经有关联了。')
      return false
    }
    const now = new Date().toISOString()
    updateProject((current) => ({
      ...current,
      updatedAt: now,
      connections: [...current.connections, { id: uid('link'), source, target, label }],
    }))
    const from = project.cards.find((card) => card.id === source)
    const to = project.cards.find((card) => card.id === target)
    notify('已建立关联：' + (from?.title || '卡片') + ' ↔ ' + (to?.title || '卡片') + '。')
    return true
  }

  function removeConnection(linkId) {
    updateProject((current) => ({
      ...current,
      updatedAt: new Date().toISOString(),
      connections: current.connections.filter((link) => link.id !== linkId),
    }))
    notify('已移除这条关联。')
  }

  function connectSelection() {
    if (selection.length !== 2) return
    addConnection(selection[0], selection[1])
    setSelection([])
    setSelectMode(false)
  }

  function openSplit(cardId) {
    setSplitCardId(cardId)
    setInspectorOpen(false)
  }

  function performSplit({ leftContent, newCard }) {
    if (!splitCard) return
    const now = new Date().toISOString()
    const newId = uid('card')
    const baseGraph = splitCard.graph || nextGraphPosition(project.cards)
    const inserted = {
      ...newCard,
      id: newId,
      graph: { x: Math.min(0.9, baseGraph.x + 0.08), y: Math.min(0.88, baseGraph.y + 0.14) },
      createdAt: now,
      updatedAt: now,
    }
    updateProject((current) => {
      const cards = current.cards.flatMap((card) => {
        if (card.id !== splitCard.id) return [card]
        return [
          { ...card, content: leftContent, summary: summaryFromContent(leftContent, card.summary), updatedAt: now },
          inserted,
        ]
      })
      return {
        ...current,
        updatedAt: now,
        cards,
        connections: [...current.connections, { id: uid('link'), source: splitCard.id, target: newId, label: '拆分' }],
      }
    })
    setSplitCardId(null)
    setSelection([])
    setSelectMode(false)
    notify('已拆成两张卡片，并在其间留了一条关联。')
  }

  function performMerge(result) {
    if (mergeCards.length < 2) return
    const base = mergeCards[0]
    const ids = mergeCards.map((card) => card.id)
    const now = new Date().toISOString()
    updateProject((current) => {
      let connections = current.connections
      if (result.keepConnections) {
        connections = connections.map((link) => ({
          ...link,
          source: ids.includes(link.source) ? base.id : link.source,
          target: ids.includes(link.target) ? base.id : link.target,
        }))
        connections = dedupe(connections)
      } else {
        connections = connections.filter((link) => !ids.includes(link.source) && !ids.includes(link.target))
      }
      const notes = mergeCards.map((card) => card.notes).filter(Boolean).join('\n\n')
      const cards = current.cards.flatMap((card) => {
        if (card.id === base.id) {
          return [{
            ...card,
            title: result.title,
            content: result.content,
            summary: result.summary,
            tags: result.tags,
            notes,
            updatedAt: now,
          }]
        }
        if (ids.includes(card.id)) return []
        return [card]
      })
      return { ...current, updatedAt: now, cards, connections }
    })
    setMergeIds([])
    setSelection([])
    setSelectMode(false)
    setSelectedCardId(base.id)
    notify('已把 ' + mergeCards.length + ' 张卡片合并为《' + result.title + '》。')
  }

  function deleteCard(cardId) {
    const card = project.cards.find((item) => item.id === cardId)
    if (!card) return
    if (!window.confirm('删除《' + card.title + '》？这张卡片上的文字也会一起删除。')) return
    const remaining = project.cards.filter((item) => item.id !== cardId)
    updateProject((current) => ({
      ...current,
      updatedAt: new Date().toISOString(),
      cards: remaining,
      connections: current.connections.filter((link) => link.source !== cardId && link.target !== cardId),
    }))
    setInspectorOpen(false)
    setSelectedCardId(remaining[0]?.id || null)
    notify('卡片已删除。')
  }

  function createCard(data) {
    const now = new Date().toISOString()
    const card = {
      ...data,
      id: uid('card'),
      graph: nextGraphPosition(project.cards),
      notes: '',
      createdAt: now,
      updatedAt: now,
    }
    updateProject((current) => ({
      ...current,
      updatedAt: now,
      cards: [...current.cards, card],
    }))
    setNewCardFor(null)
    setSelectedCardId(card.id)
    notify('新卡片已经放上桌面。')
  }

  function startAddCard(column = 'inspiration') {
    setNewCardFor({ column, act: selectedCard?.act || acts[0] || '第一幕 · 未命名' })
  }

  function openRoundtable(ids, fallback = false) {
    const context = ids?.length ? ids : (selectedCard ? [selectedCard.id] : project.cards.slice(0, 3).map((card) => card.id))
    onOpenRoundtable(context, fallback)
  }

  if (!project) return null

  return (
    <div className="workspace">
      <header className="workspace-top">
        <div className="workspace-top__left">
          <button className="icon-btn binder-toggle" onClick={() => { setBinderOpen(true); setInspectorOpen(false) }} aria-label="打开手稿大纲"><PanelLeft size={17} /></button>
          <button className="icon-btn" onClick={onBack} aria-label="返回书架"><ArrowLeft size={18} /></button>
          <MiniCover project={project} size={30} />
          <div className="workspace-title">
            <strong>{project.title}</strong>
            <span>{project.type} · 第 1 稿</span>
          </div>
          <span className="save-status"><Cloud size={14} /> {SAVE_TEXT[saveState] || '已保存'}</span>
          {onOpenHistory ? (
            <button className="icon-btn" onClick={onOpenHistory} title="版本历史与回朔" aria-label="版本历史与回朔"><History size={16} /></button>
          ) : null}
        </div>

        <nav className="view-switch" aria-label="视图切换">
          {VIEWS.map((item) => {
            const Icon = item.icon
            return (
              <button key={item.id} className={view === item.id ? 'is-active' : ''} onClick={() => changeView(item.id)}>
                <Icon size={15} /> {item.label}
              </button>
            )
          })}
        </nav>

        <div className="workspace-top__right">
          <button className="top-ai" onClick={() => openRoundtable()}>
            <div className="top-ai__avatars">
              {AI_ROLES.map((role) => (
                <span key={role.id} style={{ background: role.color }} title={role.name + ' · ' + role.title}>{role.initials}</span>
              ))}
            </div>
            AI 圆桌
          </button>
          <button className="btn btn--ink top-finish" onClick={onOpenFinish}>
            <WandSparkles size={15} /> 收束定稿
          </button>
          <Avatar name="林" size={30} />
        </div>
      </header>

      <div className="workspace-body">
        {binderOpen ? <button className="binder-scrim" onClick={() => setBinderOpen(false)} aria-label="关闭手稿大纲" /> : null}
        <Binder
          project={project}
          selectedCardId={selectedCardId}
          mobileOpen={binderOpen}
          onBack={onBack}
          onPickCard={(id) => { pickFromBinder(id); setBinderOpen(false) }}
          onAddCard={(column) => { startAddCard(column); setBinderOpen(false) }}
          onOpenFinish={onOpenFinish}
        />

        <main className={'workspace-main workspace-main--' + view}>
          {view === 'board' ? (
            <BoardView
              project={project}
              selectedCardId={inspectorOpen ? selectedCardId : null}
              onOpenCard={openCard}
              onOpenDocument={openDocument}
              onMoveCard={moveCard}
              onAddCard={startAddCard}
              onOpenRoundtable={(ids) => openRoundtable(ids)}
              selectMode={selectMode}
              setSelectMode={(next) => {
                setSelectMode(next)
                setSelection([])
                if (next) setInspectorOpen(false)
              }}
              selection={selection}
              setSelection={setSelection}
              onMergeSelection={() => setMergeIds(selection)}
              onSplitSelection={() => openSplit(selection[0])}
              onLinkSelection={connectSelection}
            />
          ) : null}

          {view === 'graph' ? (
            <GraphView
              project={project}
              selectedCardId={inspectorOpen ? selectedCardId : null}
              onOpenCard={openCard}
              onOpenDocument={openDocument}
              onMoveCardPosition={movePosition}
              onConnectCards={(source, target) => addConnection(source, target, '线索')}
              onAutoArrange={autoArrange}
              notify={notify}
            />
          ) : null}

          {view === 'document' ? (
            <DocumentView
              project={project}
              card={selectedCard || project.cards[0]}
              onChangeCard={(patch) => selectedCard && patchCard(selectedCard.id, patch)}
              onSelectCard={setSelectedCardId}
              onOpenRoundtable={(ids) => openRoundtable(ids)}
              onOpenFinish={onOpenFinish}
              onSplit={openSplit}
            />
          ) : null}
        </main>

        {inspectorOpen && selectedCard ? (
          <Inspector
            card={selectedCard}
            project={project}
            onClose={() => setInspectorOpen(false)}
            onPatch={(patch) => patchCard(selectedCard.id, patch)}
            onDelete={deleteCard}
            onSplit={openSplit}
            onOpenDocument={openDocument}
            onOpenRoundtable={(ids) => openRoundtable(ids)}
            onAddConnection={addConnection}
            onRemoveConnection={removeConnection}
            onSelectCard={(id) => setSelectedCardId(id)}
          />
        ) : null}
      </div>

      {splitCard ? (
        <SplitModal card={splitCard} onClose={() => setSplitCardId(null)} onSplit={performSplit} />
      ) : null}
      {mergeCards.length >= 2 ? (
        <MergeModal cards={mergeCards} onClose={() => setMergeIds([])} onMerge={performMerge} />
      ) : null}
      {newCardFor ? (
        <NewCardModal
          defaultColumn={newCardFor.column}
          defaultAct={newCardFor.act}
          acts={acts.length ? acts : ['第一幕 · 未命名', '第二幕 · 未命名']}
          onClose={() => setNewCardFor(null)}
          onCreate={createCard}
        />
      ) : null}
    </div>
  )
}
